import React, { useEffect, useState } from "react";
import { Form, Button, Message } from "semantic-ui-react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { useUserContext } from "../../../contexts/UserContext";
import { PHONE_NUMBER } from "../../../constants/company";
import {
  normalizeCustomerName,
  validateCustomerName,
  validateCity,
  validateEmail,
  validateOptionalSecondaryEmail,
  validatePassword,
  validateState,
  validateStreet,
} from "../../../utils/validation";

interface FieldTypes {
  givenName: string;
  familyName: string;
  email: string;
  secondaryEmail: string;
  password: string;
  confirmPassword: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
}

export const RegisterForm: React.FC<any> = () => {
  const { signUp } = useUserContext();
  const history = useHistory();
  const [hasServerError, setServerError] = useState<boolean>(false);
  const [serverErrorMessage, setServerErrorMessage] = useState<string>();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm<FieldTypes>({
    mode: "onChange",
    criteriaMode: "firstError",
    shouldUnregister: false,
    defaultValues: {
      givenName: "",
      familyName: "",
      secondaryEmail: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      phoneNumber: "",
    },
  });

  useEffect(() => {
    register("givenName", {
      required: "First name is required",
      validate: (value) => validateCustomerName(value),
    });
    register("familyName", {
      required: "Last name is required",
      validate: (value) => validateCustomerName(value),
    });
    register("email", {
      validate: (value) => validateEmail(value),
    });
    register("secondaryEmail", {
      validate: (value) =>
        validateOptionalSecondaryEmail(value, watch("email")) ||
        "Please enter a valid secondary email different from the primary email",
    });
    register("password", {
      validate: (value) => validatePassword(value),
    });
    register("confirmPassword", {
      validate: (value) => value === watch("password"),
    });
    register("street", {
      required: "Street address is required",
      validate: (value) => validateStreet(value),
    });
    register("city", {
      required: "City is required",
      validate: (value) => validateCity(value),
    });
    register("state", {
      required: "State is required",
      validate: (value) => validateState(value),
    });
    register("zip", {
      required: "Zip code is required",
      validate: (value) => value.length === 5,
    });
    register("phoneNumber", {
      required: "Phone number is required",
      validate: (value) =>
        /^\+?(\d[\d\s()-]{7,}\d)$/.test(value) || "Invalid phone number",
    });
  }, [register, watch]);

  const onSubmit = async (data: FieldTypes) => {
    setServerError(false);
    setServerErrorMessage(undefined);
    clearErrors(["email", "secondaryEmail", "phoneNumber"]);
    try {
      const response = await signUp({
        givenName: normalizeCustomerName(data.givenName),
        familyName: normalizeCustomerName(data.familyName),
        email: data.email,
        ...(data.secondaryEmail
          ? { secondaryEmail: data.secondaryEmail.trim() }
          : {}),
        password: data.password,
        street: data.street || "",
        city: data.city || "",
        state: data.state || "",
        zip: data.zip || "",
        phoneNumber: data.phoneNumber || "",
      });

      if (response.status === "success") {
        history.push("/orders");
      } else {
        const message = response.message ?? "";
        if (message.includes("Secondary email")) {
          setError("secondaryEmail", { type: "server", message });
        } else if (message.includes("Email already in use")) {
          setError("email", { type: "server", message });
        } else if (message.includes("Phone number already in use")) {
          setError("phoneNumber", { type: "server", message });
        } else {
          setServerError(true);
          setServerErrorMessage(message);
        }
      }
    } catch (error) {
      const message = error?.response?.data?.message || "An unexpected error occurred. Please try again.";
      if (message.includes("Secondary email")) {
        setError("secondaryEmail", { type: "server", message });
      } else if (message.includes("Email already in use")) {
        setError("email", { type: "server", message });
      } else if (message.includes("Phone number already in use")) {
        setError("phoneNumber", { type: "server", message });
      } else {
        setServerError(true);
        setServerErrorMessage(message);
      }
    }
  };

  return (
    <Form className="mb-4" onSubmit={handleSubmit(onSubmit)} error={hasServerError}>
      <Form.Input
        fluid
        label="First Name"
        placeholder="First Name"
        name="givenName"
        onChange={async (e: any) => {
          setValue("givenName", e.target.value);
          if (!e.target.value || validateCustomerName(e.target.value)) {
            await trigger("givenName");
          }
        }}
        onBlur={(e: any) =>
          setValue("givenName", normalizeCustomerName(e.target.value), {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.givenName
            ? {
              content:
                errors.givenName.message ||
                "First name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters",
              pointing: "above",
            }
            : undefined
        }
      />

      <Form.Input
        fluid
        label="Last Name"
        placeholder="Last Name"
        name="familyName"
        onChange={async (e: any) => {
          setValue("familyName", e.target.value);
          if (!e.target.value || validateCustomerName(e.target.value)) {
            await trigger("familyName");
          }
        }}
        onBlur={(e: any) =>
          setValue("familyName", normalizeCustomerName(e.target.value), {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.familyName
            ? {
              content:
                errors.familyName.message ||
                "Last name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Email Input */}
      <Form.Input
        fluid
        label="Email Address"
        placeholder="Email Address"
        name="email"
        onChange={async (e: any) => {
          setValue("email", e.target.value);
          if (validateEmail(e.target.value)) {
            await trigger("email");
          }
        }}
        onBlur={(e: any) =>
          setValue("email", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.email
            ? {
              content: errors.email.message || "Please enter a valid email address",
              pointing: "above",
            }
            : undefined
        }
      />

      <Form.Input
        fluid
        id="secondaryEmail"
        label="Secondary Email (CC notifications only)"
        placeholder="Optional secondary email"
        name="secondaryEmail"
        onChange={async (e: any) => {
          setValue("secondaryEmail", e.target.value);
          if (!e.target.value || validateEmail(e.target.value)) {
            await trigger("secondaryEmail");
          }
        }}
        onBlur={(e: any) =>
          setValue("secondaryEmail", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.secondaryEmail
            ? {
              content:
                errors.secondaryEmail.message ||
                "Please enter a valid secondary email different from the primary email",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Password Input */}
      <Form.Input
        fluid
        label="Password"
        placeholder="Enter a password"
        type="password"
        name="password"
        onChange={async (e: any) => {
          setValue("password", e.target.value);
          if (validatePassword(e.target.value)) {
            await trigger("password");
          }
        }}
        onBlur={(e: any) =>
          setValue("password", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.password
            ? {
              content: "Password must be 6 characters long",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Confirm Password Input */}
      <Form.Input
        fluid
        label="Confirm Password"
        placeholder="Confirm your password"
        type="password"
        name="confirmPassword"
        onChange={async (e: any) => {
          setValue("confirmPassword", e.target.value);
          if (e.target.value === watch("password")) {
            await trigger("confirmPassword");
          }
        }}
        onBlur={(e: any) =>
          setValue("confirmPassword", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.confirmPassword
            ? {
              content: "Passwords must match",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Street Input */}
      <Form.Input
        fluid
        label="Street"
        placeholder="Street Address"
        name="street"
        onChange={async (e: any) => {
          setValue("street", e.target.value);
          if (!e.target.value || validateStreet(e.target.value)) {
            await trigger("street");
          }
        }}
        onBlur={(e: any) =>
          setValue("street", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.street
            ? {
              content: "Please enter a valid street address",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* City Input */}
      <Form.Input
        fluid
        label="City"
        placeholder="City"
        name="city"
        onChange={async (e: any) => {
          setValue("city", e.target.value);
          if (!e.target.value || validateCity(e.target.value)) {
            await trigger("city");
          }
        }}
        onBlur={(e: any) =>
          setValue("city", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.city
            ? {
              content: "Please enter a valid city",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* State Input */}
      <Form.Input
        fluid
        label="State"
        placeholder="State"
        name="state"
        onChange={async (e: any) => {
          setValue("state", e.target.value);
          if (!e.target.value || validateState(e.target.value)) {
            await trigger("state");
          }
        }}
        onBlur={(e: any) =>
          setValue("state", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.state
            ? {
              content: "Please enter a valid state",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Zip Code Input */}
      <Form.Input
        fluid
        label="Zip Code"
        placeholder="Zip Code"
        name="zip"
        maxLength={5}
        onChange={async (e: any) => {
          const val = e.target.value;
          if (/^\d{0,5}$/.test(val)) {
            setValue("zip", val);
            if (val.length === 5) {
              await trigger("zip");
            }
          }
        }}
        onBlur={(e: any) =>
          setValue("zip", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.zip
            ? {
              content: "Please enter a 5-digit zip code",
              pointing: "above",
            }
            : undefined
        }
      />

      {/* Phone Number Input */}
      <Form.Input
        fluid
        label="Phone Number"
        placeholder="Phone Number"
        name="phoneNumber"
        onChange={async (e: any) => {
          setValue("phoneNumber", e.target.value);
          const val = e.target.value;
          if (!val || /^\+?[0-9\s()-]+$/.test(val)) {
            await trigger("phoneNumber");
          }
        }}
        onBlur={(e: any) =>
          setValue("phoneNumber", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.phoneNumber
            ? {
              content: errors.phoneNumber.message || "Please enter a valid phone number",
              pointing: "above",
            }
            : undefined
        }
        type="tel"
      />

      <Button
        type="submit"
        disabled={
          !validateCustomerName(watch("givenName")) ||
          !validateCustomerName(watch("familyName")) ||
          !validateEmail(watch("email")) ||
          !validateOptionalSecondaryEmail(
            watch("secondaryEmail"),
            watch("email")
          ) ||
          !validatePassword(watch("password")) ||
          watch("confirmPassword") !== watch("password") ||
          !watch("street") ||
          !watch("city") ||
          !watch("state") ||
          !(watch("zip")?.length === 5) ||
          !watch("phoneNumber") ||
          isSubmitting
        }
      >
        Submit
      </Button>

      <Message
        error
        header="Server Error"
        content={
          serverErrorMessage ? serverErrorMessage : `Uh oh! Our computers screwed up again. Please try again later or call us at ${PHONE_NUMBER}.`
        }
      />
    </Form>
  );
};

export default RegisterForm;
